<template>
  <div class="episode-container row">
    <div class="thumbnail-container">
      <a :href="episode.episode_path">
        <img
          class="thumbnail"
          :alt="episode.title"
          loading="lazy"
          :src="episode.thumbnail"
        />
        <div :class="{'duration-with-offset': hasProgressBar}" class="duration">{{ episode.duration }}</div>
        <div v-if="hasProgressBar" class="progress-wrapper">
          <EpisodeProgressComponent :progressBar="episode.progress_bar" />
        </div>
      </a>

      <div class="text-content">
        <a class="title" :href="episode.episode_path">{{ episode.title }}</a>

        <div v-if="episode.speakers" class="speakers-short">
          <div v-for="speaker in episode.speakers">
            <img
              class="speaker-avatar"
              :alt="speaker.full_name"
              loading="lazy"
              :src="speaker.thumbnail_url"
            />
          </div>

          <span class="speaker-name">
            {{ speakerNames() }}
          </span>
        </div>

        <div v-if="episode.series" class="footnote">
          <a :href="episode.episode_path">
            {{ `E${episode.sequence_number} | ${episode.series.title}` }}
          </a>
        </div>

        <SpecialEpisodeLabels :episode="episode" />
      </div>
    </div>

    <div class="episode-bookmarks-container col">
      <Bookmark
        v-for="bookmark of bookmarks"
        :key="bookmark.id"
        :bookmark="bookmark"
        :mode="MODES.SHOW"
        :theme="theme"
        :href="bookmarkHref(bookmark)"
        @[EMITTERS.BOOKMARK_DELETED_EMITTER]="deleteBookmark"
        @[EMITTERS.BOOKMARK_UPDATED_EMITTER]="updateBookmark"
      />
      <div
        v-if="episode.bookmarks.length > bookmarksLimit"
        class="toggle-container"
      >
        <span v-if="!expanded" class="show-more" @click="showMore">
          Show more ({{ episode.bookmarks.length - bookmarksLimit }})
          <ArrowDownIcon :theme="theme" />
        </span>
        <span v-else class="show-less" @click="showLess">
          Show less ({{ bookmarksLimit }})
          <ArrowUpIcon :theme="theme" />
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { remove } from 'lodash'

import Bookmark from '../../study_tools/components/Bookmark'
import ArrowDownIcon from '../../shared/components/icons/ArrowDown'
import ArrowUpIcon from '../../shared/components/icons/ArrowUp'
import SpecialEpisodeLabels from "../../shared/components/SpecialEpisodeLabels.vue"
import {
  THEMES,
  BOOKMARK_MODES as MODES,
} from '../../shared/utils/view_options'
import { EMITTERS } from '../../shared/utils/emitters'
import EpisodeProgressComponent from '../../shared/components/EpisodeProgress.vue'

export default {
  name: 'EpisodeComponent',
  components: {
    EpisodeProgressComponent,
    SpecialEpisodeLabels,
    ArrowUpIcon,
    ArrowDownIcon,
    Bookmark,
  },
  props: {
    episode: {
      title: String,
      id: Number,
      meta_title: String,
      thumbnail: String,
      episode_path: String,
      speakers: Array,
      duration: String,
      sequence_number: Number,
      premiering: Boolean,
      exclusive: Boolean,
      series: {
        id: Number,
        title: String,
      },
      bookmarks: {
        title: String,
        id: Number,
        timestamp_seconds: Number,
      },
    },
  },
  emits: [EMITTERS.BOOKMARK_DELETED_EMITTER],
  data() {
    return {
      MODES,
      theme: THEMES.LIGHT,
      bookmarks: [],
      expanded: false,
      bookmarksLimit: 2,
      EMITTERS,
    }
  },
  computed: {
    hasProgressBar() {
      return this.episode.progress_bar && this.episode.progress_bar.percent
    }
  },
  created() {
    this.bookmarks = this.episode.bookmarks.slice(0, this.bookmarksLimit)
  },
  methods: {
    deleteBookmark(bookmark) {
      remove(this.episode.bookmarks, (element) => element.id === bookmark.id)
      this.count--
      this.bookmarks = this.episode.bookmarks.slice(0, this.bookmarksLimit)
      if (this.expanded) {
        this.showMore()
      }
      this.$emit(EMITTERS.BOOKMARK_DELETED_EMITTER, {
        episode: this.episode,
        bookmarks: this.bookmarks,
      })
    },
    updateBookmark(bookmark) {
      const storedBookmark = this.episode.bookmarks.find(
        (element) => element.id === bookmark.id
      )
      Object.assign(storedBookmark, bookmark)
      this.$emit(EMITTERS.BOOKMARK_UPDATED_EMITTER)
    },
    bookmarkHref(bookmark) {
      return `${this.episode.episode_path}?t=${bookmark.timestamp_seconds}`
    },
    showMore(_event) {
      this.expanded = true
      this.bookmarks = this.episode.bookmarks
    },
    showLess(_event) {
      this.expanded = false
      this.bookmarks = this.episode.bookmarks.slice(0, this.bookmarksLimit)
    },
    speakerNames() {
      if (!this.episode.speakers[0]) return
      const firstSpeaker = this.episode.speakers[0].full_name
      const speakersCount = this.episode.speakers.length - 1
      if (this.episode.speakers.length === 1) return firstSpeaker

      return firstSpeaker + ' (+' + speakersCount + ')'
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../shared/assets/styles/global';

.thumbnail-container {
  width: 180px;
  height: 90px;
  position: relative;

  .thumbnail {
    object-fit: cover;
  }

  .progress-wrapper {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
  }
  .duration {
    position: absolute;
    width: 69px;
    height: 24px;
    bottom: 0;
    right: 0;
    background-color: #1c1923;
    color: $white;
    font-size: 12px;
    line-height: 24px;
    text-align: center;
    vertical-align: middle;
    font-weight: 500;
    opacity: 0.8;
  }
  .duration-with-offset {
    bottom: 4px;
  }

  img {
    width: 180px;
    height: 90px;
  }
}

.episode-container {
  width: 100%;
  min-height: 220px;
  background-color: #ffffff;
  margin-bottom: 16px;
  padding: 16px;
}

.text-content {
  display: flex;
  flex-direction: column;
  font-style: normal;
  font-weight: 500;
}

.title,
.series-title {
  text-overflow: ellipsis;
  overflow: hidden;
  width: 180px;
  white-space: nowrap;
}

.title {
  height: 18px;
  margin-top: 6px;
  font-size: 14px;
  color: #323232;
}

.series-title {
  margin-top: 2px;
  height: 18px;
  font-size: 14px;
  line-height: 18px;
  color: #5c5c5c;
}

.toggle-container {
  margin-top: 12px;
}

.show-more,
.show-less {
  cursor: pointer;
  background-color: $white;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0;
  text-align: left;
  color: $quick-silver;
}

.episode-bookmarks-container {
  padding-left: 24px;
}

.speakers-short {
  display: flex;

  .speaker-avatar {
    width: 16px !important;
    height: 16px !important;
    border: 0.5px solid #d1d3d4;
    border-radius: 50%;

    &:not(:first-child) {
      margin-left: -12px;
    }
  }

  .speaker-name {
    font-family: 'DM Sans', serif;
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    color: #5c5c5c;
    overflow: hidden;
    margin-left: 6px;
    margin-top: 8px;
  }
}
.footnote {
  line-height: 14px;
  margin-top: 3px;
  width: 180px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  a {
    color: #5c5c5c;
    font-size: 14px;
    text-decoration: none;
  }
}
</style>
